import { FC } from 'react';
import { useQuery } from 'react-query';
import { Form } from '@/components/form';
import FormButtons from '@/components/form/form-buttons';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { QueryKey } from '@/lib/query-client';
import { getOrgPolicyV1, getPolicyV1ById, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import { Grid } from '@mui/material';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { cloneDeep } from 'lodash';
import { FieldError } from '@/lib/services';
import PsAllowWarningToast from '../org-policy/ps-allow-warning-toast';
import ServiceControlsV1 from '@/components/form/policy-v1/service-controls-v1';
import RerouteControlsV1 from '@/components/form/policy-v1/reroute-controls-v1';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['publicServices', 'metadataControls', 'contentControls', 'reroute'];
const formModel = new PolicyModelV1();

interface Props {
  policyId: string;
}

const ServiceAccessForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canViewReroute } = useFeature();

  const query = useQuery([QueryKey.PolicyCompView, policyId], async () => {
    const [policy, orgPolicy] = await Promise.all([getPolicyV1ById(policyId), getOrgPolicyV1()]);

    if (policy && orgPolicy) {
      policy.applyOrgPolicy(orgPolicy);
    }

    return [policy, orgPolicy];
  });
  const { data, refetch } = query;
  const [policy, orgPolicy] = (data || [null, null]) as [
    PolicyModelV1 | null,
    PolicyModelV1 | null,
  ];

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      if (orgPolicy) {
        updateValues.stripOrgPolicy(orgPolicy);
      }

      const { patched, error, fieldErrors } = await patchPolicyV1(
        policyId,
        updateValues.propSlice(fields)
      );

      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        refetch();
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const privateModelsQuery = useQuery([QueryKey.PrivateModelsList], async () =>
    getAllPrivateModels()
  );
  const { data: privateModels = [] } = privateModelsQuery;

  const { values } = formHandle;

  const { showLoader } = useQueryHelper(query);
  const { showLoader: privateModelsLoading } = useQueryHelper(privateModelsQuery);

  if (showLoader || privateModelsLoading) {
    return;
  }

  const { allowsPublicServices } = values;

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection title="Default Service Action" scrollId="default-service-action">
        <FormSectionSubtitle>
          When an attempt is made to access a known service, choose how to handle the request.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Default Service Action"
              name="publicServices"
              type="policy-public-action-select-v1"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              policy={policy!}
              orgPolicy={orgPolicy || undefined}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Service Block List" scrollId="blocked-services">
        <FormSectionSubtitle>
          Use the table below to block services for groups in this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="publicServices"
              label="Public Services"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl-v1"
              orgPolicy={orgPolicy || undefined}
              aclVariant="block"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Service Allow List"
        scrollId="allowed-services"
        disabled={allowsPublicServices}
      >
        <FormSectionSubtitle>
          Use the table below to allow access to services for groups in this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Allowed services can be monitored or protected. Protected services apply the sensitive
            data policies to those services. Monitored services will be logged with no further
            action taken.
          </Grid>
          <Grid item xs={12}>
            <PsAllowWarningToast policy={values} />

            <InputControl
              name="publicServices"
              label="Public Services"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl-v1"
              orgPolicy={orgPolicy || undefined}
              aclVariant="allow"
            />
          </Grid>
        </Grid>
      </FormSection>

      <ServiceControlsV1
        formHandle={formHandle}
        readonly={!canChangePolicy}
        policy={policy!}
        orgPolicy={orgPolicy || undefined}
      />

      {canViewReroute && (
        <RerouteControlsV1
          formHandle={formHandle}
          readonly={!canChangePolicy}
          privateModels={privateModels}
        />
      )}

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default ServiceAccessForm;
